import * as THREE from 'three';
import { AudioEngine } from '../audio/engine';
import { GAME_CONFIG, ndviSprayMultiplier, ndviTotalSec } from '../config/gameConfig';
import type { EffectsHandles } from '../scene/effects';
import type { EnvironmentHandles } from '../scene/environment';
import {
  applyNdviScan,
  clearNdviOverlay,
  updateCropVisuals,
  updateNdviVisuals,
} from '../scene/fields';
import type { SceneContext } from '../scene/setup';
import { dronePadPos, truckParkingPos } from '../state/gameState';
import type { Field, GameState } from '../state/types';
import { createFloatingIndicator } from '../ui/floatingLabel';
import type { DispatchFn } from '../ui/sidebar';
import { updateHTML_UI } from '../ui/sidebar';
import { runAutoPilotDecisions } from './autopilot';
import { evaluateOutcome } from './outcome';

export interface SimulationContext {
  state: GameState;
  sceneCtx: SceneContext;
  env: EnvironmentHandles;
  effects: EffectsHandles;
  onDispatch: DispatchFn;
}

export function createSimulation(sim: SimulationContext): (dt: number) => void {
  let tickAccumulator = 0;
  const scannerApexWorld = new THREE.Vector3();

  return function updateSimulation(dt: number): void {
    const { state, sceneCtx, env, effects, onDispatch } = sim;
    const clampedDt = Math.min(dt, 0.1);

    if (state.phase !== 'playing') {
      return;
    }

    if (state.autoPilotActive) {
      runAutoPilotDecisions(state, sceneCtx);
    }

    env.bladesGroup.rotation.z += 1.5 * clampedDt;

    // NDVI lifetime ticks every frame for smooth fade + sidebar bar.
    state.fields.forEach((f) => {
      if (f.ndviRemaining > 0) {
        f.ndviRemaining = Math.max(0, f.ndviRemaining - clampedDt);
        updateNdviVisuals(f);
      }
    });
    updateHTML_UI(state, onDispatch);

    tickAccumulator += clampedDt;
    if (tickAccumulator >= 1.0) {
      tickAccumulator = 0;

      const { fields: fieldBal, economy } = GAME_CONFIG;
      const infectionGrowthSpan = fieldBal.infectionGrowthMax - fieldBal.infectionGrowthMin;

      const treatingFieldId = state.truck.state === 'Working' ? state.truck.targetFieldId : null;

      state.fields.forEach((f) => {
        if (f.dead) return;

        // While the pickup is spraying a field, infection only decreases (no growth / reinfection).
        if (f.id !== treatingFieldId) {
          if (f.infection > 0) {
            f.infection = Math.min(
              fieldBal.maxInfection,
              f.infection + (Math.random() * infectionGrowthSpan + fieldBal.infectionGrowthMin),
            );
          } else if (Math.random() < fieldBal.reinfectionChance) {
            f.infection = fieldBal.reinfectionAmount;
          }
        }

        if (f.infection >= fieldBal.maxInfection) {
          f.dead = true;
          f.infection = fieldBal.maxInfection;
          state.cash = Math.max(0, state.cash - economy.fieldDeathPenalty);
          clearNdviOverlay(f);
          createFloatingIndicator(sceneCtx, '💀 Crop Elpusztult!', f.x, f.z, true);
          AudioEngine.playBeep(180, 'sawtooth', 0.5);

          if (f.soilMat) f.soilMat.color.setHex(0x1c1c1c);
        }

        updateCropVisuals(f);
      });

      evaluateOutcome(state);
    }

    const currentSpeedMultiplier = state.autoPilotActive
      ? GAME_CONFIG.autopilot.speedMultiplier
      : 1.0;
    const { scanner, mistParticles } = effects;

    if (state.drone.state !== 'Idle' && state.drone.mesh) {
      const { cruiseAltitude, landAltitude, verticalSpeed } = GAME_CONFIG.units.drone;
      const vStep = verticalSpeed * currentSpeedMultiplier * clampedDt;
      const landedOnPad =
        state.drone.state === 'Returning' && state.drone.mesh.position.y <= landAltitude + 0.02;

      if (!landedOnPad) {
        state.drone.rotors.forEach((r) => {
          r.rotation.y += 0.5;
        });
      }

      const targetField = state.fields.find((f) => f.id === state.drone.targetFieldId);
      let targetX = dronePadPos.x;
      let targetZ = dronePadPos.z;

      if (state.drone.state === 'Moving' || state.drone.state === 'Working') {
        if (targetField) {
          targetX = targetField.x;
          targetZ = targetField.z;
        }
      }

      const currentPos = state.drone.mesh.position;
      const dist2D = Math.hypot(targetX - currentPos.x, targetZ - currentPos.z);

      if (state.drone.state === 'Moving') {
        scanner.setActive(false);
        if (currentPos.y < cruiseAltitude - 0.01) {
          currentPos.y = Math.min(cruiseAltitude, currentPos.y + vStep);
          state.drone.y = currentPos.y;
        } else {
          currentPos.y = cruiseAltitude;
          state.drone.y = cruiseAltitude;
          const dirX = targetX - currentPos.x;
          const dirZ = targetZ - currentPos.z;

          if (dist2D > 0.2) {
            currentPos.x +=
              (dirX / dist2D) * state.drone.speed * currentSpeedMultiplier * clampedDt;
            currentPos.z +=
              (dirZ / dist2D) * state.drone.speed * currentSpeedMultiplier * clampedDt;
          } else {
            state.drone.state = 'Working';
          }
        }
      } else if (state.drone.state === 'Working') {
        currentPos.y = cruiseAltitude;
        state.drone.y = cruiseAltitude;

        // Apex on camera lens; base corners = field soil top corners.
        if (targetField && state.drone.cameraLens) {
          scanner.setActive(true);
          state.drone.cameraLens.getWorldPosition(scannerApexWorld);
          scanner.update(scannerApexWorld, targetField);
        } else {
          scanner.setActive(false);
        }

        if (Math.random() < 0.15) {
          AudioEngine.playDroneNoise();
        }

        state.drone.progress +=
          clampedDt * GAME_CONFIG.units.drone.workProgressPerSec * currentSpeedMultiplier;
        if (state.drone.progress >= GAME_CONFIG.units.workCompleteProgress) {
          if (targetField) {
            targetField.ndviRemaining = ndviTotalSec();
            applyNdviScan(targetField);
            updateNdviVisuals(targetField);
            createFloatingIndicator(sceneCtx, '🚁 Szkennelés Kész!', targetField.x, targetField.z);
            AudioEngine.playBeep(880, 'sine', 0.15);
          }
          scanner.setActive(false);
          state.drone.state = 'Returning';
          state.drone.progress = 0;
        }
      } else if (state.drone.state === 'Returning') {
        scanner.setActive(false);
        const dirX = dronePadPos.x - currentPos.x;
        const dirZ = dronePadPos.z - currentPos.z;

        if (dist2D > 0.2) {
          currentPos.y = cruiseAltitude;
          state.drone.y = cruiseAltitude;
          currentPos.x += (dirX / dist2D) * state.drone.speed * currentSpeedMultiplier * clampedDt;
          currentPos.z += (dirZ / dist2D) * state.drone.speed * currentSpeedMultiplier * clampedDt;
        } else if (currentPos.y > landAltitude + 0.01) {
          currentPos.x = dronePadPos.x;
          currentPos.z = dronePadPos.z;
          currentPos.y = Math.max(landAltitude, currentPos.y - vStep);
          state.drone.y = currentPos.y;
          state.drone.progress = 0;
        } else {
          currentPos.x = dronePadPos.x;
          currentPos.z = dronePadPos.z;
          currentPos.y = landAltitude;
          state.drone.y = landAltitude;
          state.drone.progress += clampedDt;
          if (state.drone.progress >= GAME_CONFIG.units.homeDwellSec) {
            state.drone.state = 'Idle';
            state.drone.targetFieldId = null;
            state.drone.task = null;
            state.drone.progress = 0;
          }
        }
      }
    } else {
      scanner.setActive(false);
    }

    if (state.truck.state !== 'Idle' && state.truck.mesh) {
      const targetField = state.fields.find((f) => f.id === state.truck.targetFieldId);
      let targetX = truckParkingPos.x;
      let targetZ = truckParkingPos.z;

      if (state.truck.state === 'Moving' || state.truck.state === 'Working') {
        if (targetField) {
          targetX = targetField.x;
          targetZ = targetField.z;
        }
      }

      const currentPos = state.truck.mesh.position;
      const dist2D = Math.hypot(targetX - currentPos.x, targetZ - currentPos.z);

      if (state.truck.state === 'Moving') {
        const dirX = targetX - currentPos.x;
        const dirZ = targetZ - currentPos.z;

        if (dist2D > 0.2) {
          const angle = Math.atan2(dirZ, dirX);
          state.truck.mesh.rotation.y = -angle;

          currentPos.x += (dirX / dist2D) * state.truck.speed * currentSpeedMultiplier * clampedDt;
          currentPos.z += (dirZ / dist2D) * state.truck.speed * currentSpeedMultiplier * clampedDt;
        } else {
          state.truck.state = 'Working';
          // Snapshot infection at spray start — reward equals this amount when cleared.
          state.truck.progress = targetField ? targetField.infection : 0;
        }
      } else if (state.truck.state === 'Working') {
        if (Math.random() < 0.25) {
          AudioEngine.playSprayNoise();
        }

        mistParticles.forEach((p) => {
          if (!p.mesh.visible) {
            p.mesh.visible = true;
            p.life = 0;
            const side = Math.random() > 0.5 ? -1 : 1;
            // Spawn mist behind the cab toward the bed (~scaled truck length).
            const bedOffset = 0.95;
            p.mesh.position.set(
              currentPos.x - Math.cos(state.truck.mesh!.rotation.y) * bedOffset,
              0.3,
              currentPos.z + Math.sin(state.truck.mesh!.rotation.y) * bedOffset + side * 0.4,
            );
            p.velocity.set(
              (Math.random() - 0.5) * 1.5,
              0.5 + Math.random() * 1.0,
              (Math.random() - 0.5) * 1.5,
            );
          } else {
            p.life += clampedDt;
            p.mesh.position.addScaledVector(p.velocity, clampedDt);
            p.mesh.material.opacity = (1.0 - p.life / p.maxLife) * 0.6;
            p.mesh.scale.setScalar(1.0 + p.life * 2.0);

            if (p.life >= p.maxLife) {
              p.mesh.visible = false;
            }
          }
        });

        const finishTreatment = (field: Field | undefined, award: boolean): void => {
          mistParticles.forEach((p) => {
            p.mesh.visible = false;
          });

          if (field && award) {
            const reward = Math.round(state.truck.progress);
            if (reward > 0) {
              state.cash += reward;
              createFloatingIndicator(sceneCtx, `+$${reward} (Kezelés)`, field.x, field.z);
              AudioEngine.playBeep(523, 'sine', 0.25);
              evaluateOutcome(state);
            }
          }

          state.truck.state = 'Returning';
          state.truck.progress = 0;
        };

        if (!targetField || targetField.dead) {
          finishTreatment(targetField, false);
        } else if (targetField.infection <= 0) {
          targetField.infection = 0;
          finishTreatment(targetField, true);
        } else {
          const cleanRate =
            GAME_CONFIG.units.truck.cleanRatePerSec *
            currentSpeedMultiplier *
            ndviSprayMultiplier(targetField.ndviRemaining);
          targetField.infection = Math.max(0, targetField.infection - cleanRate * clampedDt);
          updateCropVisuals(targetField);

          if (targetField.infection <= 0) {
            targetField.infection = 0;
            finishTreatment(targetField, true);
          }
        }
      } else if (state.truck.state === 'Returning') {
        const dirX = truckParkingPos.x - currentPos.x;
        const dirZ = truckParkingPos.z - currentPos.z;

        if (dist2D > 0.2) {
          const angle = Math.atan2(dirZ, dirX);
          state.truck.mesh.rotation.y = -angle;

          currentPos.x += (dirX / dist2D) * state.truck.speed * currentSpeedMultiplier * clampedDt;
          currentPos.z += (dirZ / dist2D) * state.truck.speed * currentSpeedMultiplier * clampedDt;
          state.truck.progress = 0;
        } else {
          currentPos.x = truckParkingPos.x;
          currentPos.z = truckParkingPos.z;
          state.truck.progress += clampedDt;
          if (state.truck.progress >= GAME_CONFIG.units.homeDwellSec) {
            state.truck.state = 'Idle';
            state.truck.targetFieldId = null;
            state.truck.task = null;
            state.truck.progress = 0;
          }
        }
      }
    } else {
      mistParticles.forEach((p) => {
        p.mesh.visible = false;
      });
    }
  };
}
